import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { SidebarSimpleIcon } from '@phosphor-icons/react'
import {
    ScoopText,
    ScoopButton,
    ScoopDialog,
    ScoopDrawer,
    ScoopSpinner,
    ScoopCenter,
    ScoopTab,
    useToast,
} from '../../ui'
import { useDomainIntelligence } from '../../../api/hooks/domainIntelligence/useDomainIntelligence'
import { useDomainIntelligenceArtifacts } from '../../../api/hooks/domainIntelligence/useDomainIntelligenceArtifacts'
import {
    useActionPlanCandidates,
    useAcceptActionPlanCandidate,
    useRejectActionPlanCandidate,
    useCreateActionPlanCandidate,
} from '../../../api/hooks/domainIntelligence/useActionCandidates'
import { useGroups, useRunGroup } from '../../../api/hooks/domainIntelligence/useGroups'
import { useTerminology } from '../../../api/hooks/domainIntelligence/useTerminology'
import { ConfigurationNav, getNavItemInfo } from './components/ConfigurationNav/ConfigurationNav'
import { MetadataForm } from './components/forms/MetadataForm/MetadataForm'
import { OrchestrationForm } from './components/forms/OrchestrationForm/OrchestrationForm'
import { QueryPatternsForm } from './components/forms/QueryPatternsForm/QueryPatternsForm'
import { DefinitionsForm } from './components/forms/DefinitionsForm/DefinitionsForm'
import { ContextForm } from './components/forms/ContextForm/ContextForm'
import { HistoryViewer } from './components/forms/HistoryViewer/HistoryViewer'
import { InvestigationReport } from './components/InvestigationReport/InvestigationReport'
import { CreateContextModal } from './components/CreateContextModal/CreateContextModal'
import { JsonEditor } from './components/JsonEditor/JsonEditor'
import { RunBrowserTree } from './components/RunBrowser/RunBrowserTree'
import { RollupReport } from './components/RollupReport/RollupReport'
import { EntityReportPage } from './components/EntityReportPage/EntityReportPage'
import { ActiveActionsPanel } from './components/ActionCandidates/ActiveActionsPanel'
import { GroupRunsPanel } from './components/GroupRuns/GroupRunsPanel'
import { NewRunConfigDialog } from './components/GroupRuns/NewRunConfigDialog'
import { ReportState } from './components/ReportState/ReportState'
import { getEntityId, getEntityDisplayId } from './utils/hierarchyUtils'
import styles from './DomainIntelligence.module.scss'

const MODE_INVESTIGATIONS = 'investigations'
const MODE_ACTIONS = 'actions'
const MODE_PLAYBOOK = 'playbook'

const parseArtifactStartedAt = (artifactRun) => {
    const value = artifactRun?.timestamp || artifactRun?.pathTimestamp
    if (!value) return 0
    const parsed = Date.parse(value)
    return Number.isNaN(parsed) ? 0 : parsed
}

const artifactRunId = (artifactRun, index) => {
    const leaf =
        artifactRun?.runPath
            ?.split('/')
            .filter(Boolean)
            .pop()
            ?.replace(/[^a-zA-Z0-9_-]/g, '_') || `artifact-${index}`
    return `artifact-${leaf}`
}

const artifactRunLabel = (artifactRun, runId) => {
    const context = Array.isArray(artifactRun?.contexts) ? artifactRun.contexts[0] : null
    if (context) return context.replace(/_/g, ' ')
    return `Artifact ${runId.replace(/^artifact-/, '').slice(0, 18)}`
}

const toRunFromArtifact = (artifactRun, index) => {
    const runId = artifactRunId(artifactRun, index)
    const startedAt = parseArtifactStartedAt(artifactRun)
    const totalTasks =
        artifactRun?.totalInvestigations ||
        artifactRun?.hierarchyEntityCount ||
        artifactRun?.artifactCount ||
        0

    return {
        runId,
        planId: null,
        status: artifactRun?.status || 'succeeded',
        triggerType: 'artifact',
        startedAt,
        endedAt: startedAt,
        totalTasks,
        completedTasks: totalTasks,
        failedTasks: 0,
        runPath: artifactRun?.runPath,
        flaggedEntitiesSummary: Array.isArray(artifactRun?.flaggedEntitiesSummary)
            ? artifactRun.flaggedEntitiesSummary
            : [],
        hierarchyLevels: Array.isArray(artifactRun?.hierarchyLevels)
            ? artifactRun.hierarchyLevels
            : [],
        entityType: artifactRun?.entityType,
        hasHierarchy: Boolean(artifactRun?.hasHierarchy),
        artifactCount: artifactRun?.artifactCount || 0,
        artifactLabel: artifactRunLabel(artifactRun, runId),
    }
}

export const DomainIntelligence = () => {
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const toast = useToast()
    const [mode, setMode] = useState(MODE_INVESTIGATIONS)
    // Left-drawer visibility (Investigations mode) — reclaim the 280px when
    // the operator wants the report full-width.
    const [drawerOpen, setDrawerOpen] = useState(true)
    const [selectedItem, setSelectedItem] = useState(null)
    const [selectedFilePath, setSelectedFilePath] = useState(null)
    const [selectedRunPath, setSelectedRunPath] = useState(null)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [pendingNavigation, setPendingNavigation] = useState(null)

    // Investigation selection state
    const [selectedTreeNode, setSelectedTreeNode] = useState(null)
    const [selectedEntityContext, setSelectedEntityContext] = useState(null)

    // Group runs (the one run method) — shares the react-query cache with
    // GroupRunsPanel, so this adds no extra request. The new-run dialog lives
    // at this level so BOTH the Launch panel and the tree's empty state can
    // open it (design §4: empty states resolve to an action).
    const { groups } = useGroups(workspaceID)
    const runMutation = useRunGroup(workspaceID)
    const [newRunSeed, setNewRunSeed] = useState(null) // null = closed, '' = blank
    // Customer-language labels from the workspace's own terminology config.
    const terminology = useTerminology(workspaceID)
    const {
        data: actionCandidatesData,
        isLoading: loadingActionCandidates,
        error: actionCandidatesError,
    } = useActionPlanCandidates(workspaceID)
    const acceptActionMutation = useAcceptActionPlanCandidate(workspaceID)
    const rejectActionMutation = useRejectActionPlanCandidate(workspaceID)
    const createActionMutation = useCreateActionPlanCandidate(workspaceID)
    const actionCandidates = actionCandidatesData?.candidates || []
    const actionCandidateCounts = actionCandidatesData?.counts

    // Per-card pending (review D22): only the card whose action is in flight
    // shows loading; the rest stay interactive-disabled, not spinner-blurred.
    const pendingActionFingerprint =
        (acceptActionMutation.isPending && acceptActionMutation.variables) ||
        (rejectActionMutation.isPending && rejectActionMutation.variables?.candidateFingerprint) ||
        null

    const {
        files,
        loadingFiles,
        filesError,
        fileContentError,
        refetchFileContent,
        fileContent,
        loadingFileContent,
        updateFile,
        isUpdating,
    } = useDomainIntelligence(workspaceID, selectedFilePath)

    const {
        artifactRuns,
        artifactRunLimits,
        loadingArtifactRuns,
        report,
        loadingReport,
        reportError,
        generateArtifactDocx,
        isGeneratingArtifactDocx,
    } = useDomainIntelligenceArtifacts(workspaceID, selectedRunPath)

    // "Make silence loud" (audit F9): the backend caps the run listing; say so
    // when runs were dropped rather than letting them silently vanish.
    const runListTruncated = Boolean(
        artifactRunLimits &&
            artifactRunLimits.maxArtifactRuns != null &&
            artifactRunLimits.artifactRunCount != null &&
            artifactRunLimits.artifactRunCount >= artifactRunLimits.maxArtifactRuns
    )

    // Every run in the browser is an investigation-group artifact run.
    const displayRuns = useMemo(
        () => artifactRuns.map((artifactRun, index) => toRunFromArtifact(artifactRun, index)),
        [artifactRuns]
    )

    const actionScopeOptions = useMemo(() => {
        const byKey = new Map()
        const addOption = (option) => {
            if (!option?.label || !option?.value) return
            const key = `${option.value}:${option.levelName || ''}`
            if (!byKey.has(key)) byKey.set(key, option)
        }

        addOption({ label: 'Portfolio', value: 'portfolio', levelName: '' })

        for (const run of displayRuns) {
            for (const levelName of run.hierarchyLevels || []) {
                addOption({ label: levelName, value: 'hierarchy_node', levelName })
            }
            if (run.entityType) {
                addOption({ label: run.entityType, value: 'entity', levelName: run.entityType })
            }
        }

        for (const artifactRun of artifactRuns) {
            for (const levelName of artifactRun.hierarchyLevels || []) {
                addOption({ label: levelName, value: 'hierarchy_node', levelName })
            }
            if (artifactRun.entityType) {
                addOption({
                    label: artifactRun.entityType,
                    value: 'entity',
                    levelName: artifactRun.entityType,
                })
            }
        }

        for (const candidate of actionCandidates) {
            if (candidate.scopeType === 'hierarchy_node' && candidate.scopeLevelName) {
                addOption({
                    label: candidate.scopeLevelName,
                    value: 'hierarchy_node',
                    levelName: candidate.scopeLevelName,
                })
            }
            if (
                (candidate.scopeType === 'entity' || candidate.scopeEntityId) &&
                candidate.scopeLevelName
            ) {
                addOption({
                    label: candidate.scopeLevelName,
                    value: 'entity',
                    levelName: candidate.scopeLevelName,
                })
            }
        }

        return Array.from(byKey.values())
    }, [actionCandidates, artifactRuns, displayRuns])

    // Extract existing context names for duplicate validation
    const existingContexts = useMemo(() => {
        return files
            .filter(
                (f) =>
                    f.path?.startsWith('contexts/') &&
                    f.path?.endsWith('.json') &&
                    !f.path?.includes('_index')
            )
            .map((f) => f.path.replace('contexts/', '').replace('.json', ''))
    }, [files])

    const applyItemSelection = useCallback((itemKey, filePath) => {
        setSelectedItem(itemKey)
        setSelectedFilePath(filePath)
        setHasUnsavedChanges(false)
    }, [])

    const handleItemSelection = useCallback(
        (itemKey, filePath) => {
            if (itemKey === selectedItem && filePath === selectedFilePath) {
                return
            }

            if (hasUnsavedChanges) {
                setPendingNavigation({ itemKey, filePath })
                return
            }

            applyItemSelection(itemKey, filePath)
        },
        [applyItemSelection, hasUnsavedChanges, selectedItem, selectedFilePath]
    )

    const handleDirtyStateChange = useCallback((isDirty) => {
        setHasUnsavedChanges(Boolean(isDirty))
    }, [])

    const confirmNavigation = useCallback(() => {
        if (!pendingNavigation) return

        applyItemSelection(pendingNavigation.itemKey, pendingNavigation.filePath)
        setPendingNavigation(null)
    }, [applyItemSelection, pendingNavigation])

    const cancelNavigation = useCallback(() => {
        setPendingNavigation(null)
    }, [])

    const handleCreateContext = useCallback(() => {
        setShowCreateModal(true)
    }, [])

    const handleContextCreate = useCallback(
        (contextData) => {
            const filePath = `contexts/${contextData.name}.json`
            // Scaffold matches the production context shape (audit F17):
            // spawn_enabled / spawn_guidance / synthesis_options live INSIDE
            // `investigation`; applicable_datasets inside `metadata`; no phantom
            // top-level keys.
            const content = {
                name: contextData.name,
                metadata: contextData.metadata,
                definitions: {},
                investigation: {
                    spawn_enabled: true,
                    probes: [],
                    patterns: {},
                    interpretation_guidance: {},
                    spawn_guidance: [],
                    termination_conditions: [],
                    synthesis_options: {},
                },
                business_context: {},
            }

            updateFile({
                filePath,
                content,
                onSuccess: () => {
                    setShowCreateModal(false)
                    toast.success(
                        `Context "${contextData.metadata.display_name}" created successfully`
                    )
                    applyItemSelection(`context:${contextData.name}`, filePath)
                },
                onError: (error) => {
                    toast.error(error?.message || 'Failed to create context')
                },
            })
        },
        [applyItemSelection, toast, updateFile]
    )

    const handleSave = useCallback(
        (content) => {
            updateFile({
                filePath: selectedFilePath,
                content,
                onSuccess: () => {
                    toast.success('Changes saved successfully')
                },
                onError: (error) => {
                    toast.error(error?.message || 'Failed to save changes')
                },
            })
        },
        [selectedFilePath, updateFile, toast]
    )

    const clearSelections = useCallback(() => {
        setSelectedTreeNode(null)
        setSelectedEntityContext(null)
        setSelectedRunPath(null)
    }, [])

    const handleNewRun = useCallback((seed) => {
        setNewRunSeed(seed ?? '')
    }, [])

    const handleLaunchNewRun = useCallback(
        (configText) => {
            runMutation.mutate(configText, {
                onSuccess: (response) => {
                    toast.success(`Run started: ${response?.groupId}`)
                    setNewRunSeed(null)
                },
                onError: (error) => {
                    toast.error(error?.message || 'Failed to start run')
                },
            })
        },
        [runMutation, toast]
    )

    const handleAcceptAction = useCallback(
        (candidate) => {
            if (!candidate?.candidateFingerprint) return
            acceptActionMutation.mutate(candidate.candidateFingerprint, {
                onSuccess: () => toast.success('Action accepted'),
                onError: (error) => toast.error(error?.message || 'Failed to accept action'),
            })
        },
        [acceptActionMutation, toast]
    )

    const handleRejectAction = useCallback(
        (candidate, reason = '') => {
            if (!candidate?.candidateFingerprint) return
            rejectActionMutation.mutate(
                {
                    candidateFingerprint: candidate.candidateFingerprint,
                    reason,
                },
                {
                    onSuccess: () => toast.success('Action rejected'),
                    onError: (error) => toast.error(error?.message || 'Failed to reject action'),
                }
            )
        },
        [rejectActionMutation, toast]
    )

    const handleCreateAction = useCallback(
        (candidate) => {
            createActionMutation.mutate(candidate, {
                onSuccess: () => toast.success('Action added'),
                onError: (error) => toast.error(error?.message || 'Failed to add action'),
            })
        },
        [createActionMutation, toast]
    )

    const handleViewEntityReport = useCallback(
        (entity, parentNode = selectedTreeNode) => {
            const entityId = getEntityId(entity)
            if (!entityId || !entity?.runPath) return

            setSelectedRunPath(entity.runPath)
            setSelectedEntityContext({
                entityId,
                entity,
                parentNode,
                hierarchyLevels: parentNode?.hierarchyLevels || [],
            })
        },
        [selectedTreeNode]
    )

    const handleTreeNodeSelect = useCallback(
        (node) => {
            clearSelections()
            const primaryRunPath = node.runPath || node.runs?.find((run) => run.runPath)?.runPath
            switch (node.nodeType) {
                case 'entity':
                    if (node.runPath) {
                        setSelectedRunPath(node.runPath)
                        setSelectedEntityContext({
                            entityId: getEntityId(node),
                            entity: node,
                            hierarchyLevels: node.hierarchyLevels || [],
                        })
                    }
                    break
                case 'artifact_run':
                    if (node.runPath) setSelectedRunPath(node.runPath)
                    break
                case 'date':
                case 'hierarchy_node':
                    if (primaryRunPath) setSelectedRunPath(primaryRunPath)
                    setSelectedTreeNode(node)
                    break
                default:
                    break
            }
        },
        [clearSelections]
    )

    const handleBackFromRollup = useCallback(() => {
        setSelectedTreeNode(null)
        setSelectedEntityContext(null)
        setSelectedRunPath(null)
    }, [])

    const handleBackFromEntityReport = useCallback(() => {
        setSelectedEntityContext(null)
    }, [])

    // Breadcrumb drill path (design: navigation leaves tracks). Crumbs mirror
    // the view stack: Investigations root → group (run report) → cycle/node
    // (rollup) → entity. Parent crumbs pop to that depth; the current view is
    // the unlinked tail.
    const breadcrumbs = useMemo(() => {
        const crumbs = [{ label: 'Investigations', onClick: clearSelections }]
        const parentNode = selectedEntityContext?.parentNode || selectedTreeNode
        const displayRun = displayRuns.find((run) => run.runPath === selectedRunPath)

        if (!selectedEntityContext && !selectedTreeNode && !selectedRunPath) {
            return crumbs
        }

        const groupLabel =
            parentNode?.groupLabel || parentNode?.planName || displayRun?.artifactLabel
        const runPath =
            parentNode?.runPath ||
            parentNode?.runs?.find((run) => run.runPath)?.runPath ||
            selectedRunPath

        if (groupLabel) {
            const isCurrent = !selectedEntityContext && !selectedTreeNode
            crumbs.push({
                label: groupLabel,
                onClick:
                    !isCurrent && runPath
                        ? () => {
                              clearSelections()
                              setSelectedRunPath(runPath)
                          }
                        : undefined,
            })
        }

        if (
            parentNode &&
            (parentNode.nodeType === 'date' || parentNode.nodeType === 'hierarchy_node')
        ) {
            crumbs.push({
                label:
                    parentNode.nodeType === 'hierarchy_node'
                        ? parentNode.nodeId
                        : parentNode.dateLabel,
                onClick: selectedEntityContext ? handleBackFromEntityReport : undefined,
            })
        }

        if (selectedEntityContext) {
            crumbs.push({
                label:
                    getEntityDisplayId(selectedEntityContext.entity) ||
                    selectedEntityContext.entityId,
            })
        }

        return crumbs
    }, [
        selectedEntityContext,
        selectedTreeNode,
        selectedRunPath,
        displayRuns,
        clearSelections,
        handleBackFromEntityReport,
    ])

    // Determine which form to render based on selected item (Playbook mode)
    const renderForm = () => {
        if (!selectedItem) {
            return (
                <ReportState
                    title="Select a configuration item"
                    message="Choose an item from the navigation panel to view and edit its settings"
                />
            )
        }

        const navInfo = getNavItemInfo(selectedItem)
        const selectedPath = navInfo?.filePath || selectedFilePath

        if (fileContentError) {
            return (
                <ReportState
                    variant="error"
                    title="Error loading file"
                    message={`Could not load ${selectedPath || 'the selected configuration'} — ${fileContentError.message}`}
                    action={
                        <ScoopButton
                            variant="default"
                            size="sm"
                            onClick={() => refetchFileContent?.()}
                            disabled={loadingFileContent}
                            loading={loadingFileContent}
                        >
                            Retry
                        </ScoopButton>
                    }
                />
            )
        }

        // Render specialized forms based on the type
        switch (navInfo?.type) {
            case 'overview':
                return (
                    <MetadataForm
                        data={fileContent}
                        onSave={handleSave}
                        isSaving={isUpdating}
                        isLoading={loadingFileContent}
                        onDirtyStateChange={handleDirtyStateChange}
                    />
                )

            case 'definitions':
                return (
                    <DefinitionsForm
                        data={fileContent}
                        onSave={handleSave}
                        isSaving={isUpdating}
                        isLoading={loadingFileContent}
                        onDirtyStateChange={handleDirtyStateChange}
                    />
                )

            case 'orchestration':
                return (
                    <OrchestrationForm
                        data={fileContent}
                        onSave={handleSave}
                        isSaving={isUpdating}
                        isLoading={loadingFileContent}
                        onDirtyStateChange={handleDirtyStateChange}
                    />
                )

            case 'query_patterns':
                return (
                    <QueryPatternsForm
                        data={fileContent}
                        onSave={handleSave}
                        isSaving={isUpdating}
                        isLoading={loadingFileContent}
                        onDirtyStateChange={handleDirtyStateChange}
                    />
                )

            case 'learning_history':
                return <HistoryViewer data={fileContent} isLoading={loadingFileContent} />

            case 'context':
                return (
                    <ContextForm
                        data={fileContent}
                        contextName={navInfo.contextName}
                        onSave={handleSave}
                        isSaving={isUpdating}
                        isLoading={loadingFileContent}
                        onDirtyStateChange={handleDirtyStateChange}
                    />
                )

            default:
                // Fallback to JSON editor
                return (
                    <JsonEditor
                        filePath={selectedFilePath}
                        content={fileContent}
                        isLoading={loadingFileContent}
                        onSave={handleSave}
                        isSaving={isUpdating}
                        onDirtyStateChange={handleDirtyStateChange}
                    />
                )
        }
    }

    // Unified Investigations main content — priority-based rendering
    const renderInvestigationsContent = () => {
        if (selectedEntityContext) {
            return (
                <EntityReportPage
                    report={report}
                    loadingReport={loadingReport}
                    reportError={reportError}
                    selectedRunPath={selectedRunPath}
                    selectedEntity={selectedEntityContext}
                    actionCandidates={actionCandidates}
                    onBack={handleBackFromEntityReport}
                    onAcceptAction={handleAcceptAction}
                    onRejectAction={handleRejectAction}
                    acceptingAction={acceptActionMutation.isPending}
                    rejectingAction={rejectActionMutation.isPending}
                    pendingFingerprint={pendingActionFingerprint}
                    breadcrumbs={breadcrumbs}
                />
            )
        }

        if (
            selectedTreeNode?.nodeType === 'date' ||
            selectedTreeNode?.nodeType === 'hierarchy_node'
        ) {
            return (
                <RollupReport
                    dateNode={selectedTreeNode}
                    report={report}
                    loadingReport={loadingReport}
                    reportError={reportError}
                    actionCandidates={actionCandidates}
                    terminology={terminology}
                    onViewEntity={(entity) => handleViewEntityReport(entity, selectedTreeNode)}
                    onBack={handleBackFromRollup}
                    breadcrumbs={breadcrumbs}
                />
            )
        }

        if (selectedRunPath) {
            return (
                <InvestigationReport
                    report={report}
                    loadingReport={loadingReport}
                    reportError={reportError}
                    selectedRunPath={selectedRunPath}
                    generateArtifactDocx={generateArtifactDocx}
                    isGeneratingArtifactDocx={isGeneratingArtifactDocx}
                    actionCandidates={actionCandidates}
                    onAcceptAction={handleAcceptAction}
                    onRejectAction={handleRejectAction}
                    acceptingAction={acceptActionMutation.isPending}
                    rejectingAction={rejectActionMutation.isPending}
                    pendingFingerprint={pendingActionFingerprint}
                    onViewEntity={(entity) => handleViewEntityReport(entity)}
                    breadcrumbs={breadcrumbs}
                    terminology={terminology}
                />
            )
        }

        // Empty state
        return (
            <ReportState
                title="Select a run"
                message={
                    'Pick a run on the left to see its report, or expand it to drill into ' +
                    'hierarchy groups and entities. Use "Run again" on a completed run to ' +
                    'start a new cycle with the same configuration.'
                }
            />
        )
    }

    // Render content based on loading/error states
    const renderContent = () => {
        if (filesError && mode === MODE_PLAYBOOK) {
            return (
                <ReportState
                    variant="error"
                    title="Error loading configuration"
                    message={filesError.message}
                />
            )
        }

        if (loadingFiles && mode === MODE_PLAYBOOK) {
            return (
                <ScoopCenter className={styles.loadingState}>
                    <ScoopSpinner size={32} />
                </ScoopCenter>
            )
        }

        if (mode === MODE_ACTIONS) {
            return (
                <div className={styles.singlePaneContent}>
                    <ActiveActionsPanel
                        candidates={actionCandidates}
                        counts={actionCandidateCounts}
                        loading={loadingActionCandidates}
                        error={actionCandidatesError}
                        onAccept={handleAcceptAction}
                        onReject={handleRejectAction}
                        onCreate={handleCreateAction}
                        accepting={acceptActionMutation.isPending}
                        rejecting={rejectActionMutation.isPending}
                        creating={createActionMutation.isPending}
                        pendingFingerprint={pendingActionFingerprint}
                        scopeOptions={actionScopeOptions}
                    />
                </div>
            )
        }

        if (mode === MODE_INVESTIGATIONS) {
            return (
                <div className={styles.layout}>
                    <ScoopDrawer open={drawerOpen} anchor="left" variant="persistent" width={280}>
                        <div className={styles.drawerContent}>
                            <div className={styles.investigationsDrawer}>
                                <div className={styles.investigationsDrawerHeader}>
                                    <ScoopText className={styles.investigationsDrawerTitle}>
                                        Investigations
                                    </ScoopText>
                                </div>
                                {/* The one real run method: investigation-group
                                    runs, shared with the CLI. The legacy "New Plan"
                                    flow is retired from the primary UI (consolidation
                                    decision, 2026-07-27). */}
                                <GroupRunsPanel workspaceID={workspaceID} onNewRun={handleNewRun} />
                                {runListTruncated && (
                                    <ScoopText className={styles.runListNotice}>
                                        Showing the {artifactRunLimits.maxArtifactRuns} most recent
                                        runs — older runs are not listed.
                                    </ScoopText>
                                )}
                                <ScoopText className={styles.browseLabel}>
                                    Browse results
                                </ScoopText>
                                <RunBrowserTree
                                    runs={displayRuns}
                                    groups={groups}
                                    loadingRuns={loadingArtifactRuns}
                                    onNodeSelect={handleTreeNodeSelect}
                                    selectedTreeNodeId={selectedTreeNode?.id || null}
                                    selectedEntityId={selectedEntityContext?.entityId || null}
                                    selectedRunPath={selectedRunPath}
                                    onNewRun={handleNewRun}
                                />
                            </div>
                        </div>
                    </ScoopDrawer>
                    <div
                        className={`${styles.mainContent} ${drawerOpen ? '' : styles.mainContentExpanded}`}
                    >
                        {renderInvestigationsContent()}
                    </div>
                </div>
            )
        }

        // MODE_PLAYBOOK
        return (
            <div className={styles.layout}>
                <ScoopDrawer open={true} anchor="left" variant="persistent" width={280}>
                    <div className={styles.drawerContent}>
                        <ConfigurationNav
                            files={files}
                            selectedItem={selectedItem}
                            onItemSelect={handleItemSelection}
                            onCreateContext={handleCreateContext}
                        />
                    </div>
                </ScoopDrawer>
                <div className={styles.mainContent}>{renderForm()}</div>
            </div>
        )
    }

    return (
        <>
            <div className={styles.container}>
                {/* Sub-nav with mode switcher */}
                <div className={styles.subNav}>
                    {mode === MODE_INVESTIGATIONS && (
                        <ScoopButton
                            variant="grey"
                            size="xs"
                            onClick={() => setDrawerOpen((open) => !open)}
                            title={drawerOpen ? 'Hide the runs drawer' : 'Show the runs drawer'}
                            className={styles.drawerToggle}
                        >
                            <SidebarSimpleIcon size={16} />
                        </ScoopButton>
                    )}
                    <ScoopText className={styles.subNavTitle}>Domain Intelligence</ScoopText>
                    <ScoopTab
                        tabs={[
                            { key: MODE_INVESTIGATIONS, label: 'Investigations' },
                            { key: MODE_ACTIONS, label: 'Actions' },
                            { key: MODE_PLAYBOOK, label: 'Playbook' },
                        ]}
                        value={mode}
                        onChange={(_, val) => setMode(val)}
                        variant="pill"
                        size="sm"
                        fullWidth={false}
                        className={styles.modeSwitcher}
                        tabClassName={styles.modeTab}
                    />
                </div>

                {/* Main Layout */}
                {renderContent()}
            </div>

            <CreateContextModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreate={handleContextCreate}
                existingContexts={existingContexts}
                isCreating={isUpdating}
            />

            <NewRunConfigDialog
                open={newRunSeed !== null}
                initialConfig={newRunSeed || ''}
                onClose={() => setNewRunSeed(null)}
                onLaunch={handleLaunchNewRun}
                isLaunching={runMutation.isPending}
            />

            <ScoopDialog
                open={Boolean(pendingNavigation)}
                onClose={cancelNavigation}
                title="Unsaved changes"
                maxWidth="sm"
                actions={
                    <>
                        <ScoopButton variant="grey" onClick={cancelNavigation}>
                            Stay
                        </ScoopButton>
                        <ScoopButton variant="default" onClick={confirmNavigation}>
                            Discard Changes and Continue
                        </ScoopButton>
                    </>
                }
            >
                <ScoopText>
                    You have unsaved changes. Discard them and continue to a new item?
                </ScoopText>
            </ScoopDialog>

        </>
    )
}
